import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ChevronLeft, ChevronRight, Dumbbell, Clock, CheckCircle } from 'lucide-react';
import * as planService from '../../../services/planService';

/**
 * Workout Calendar Component
 * Weekly/daily view of scheduled workouts with progress indicators
 */
export default function WorkoutCalendar({ planId }) {
  const [workouts, setWorkouts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'day'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (planId) {
      loadWorkouts();
    }
  }, [planId, currentDate]);

  const loadWorkouts = async () => {
    setIsLoading(true);
    try {
      const startDate = new Date(currentDate);
      startDate.setDate(startDate.getDate() - startDate.getDay()); // Start of week
      
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6); // End of week

      const result = await planService.getWorkouts(planId, {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
      });

      if (result.success) {
        setWorkouts(result.data || []);
      }
    } catch (error) {
      console.error('Error loading workouts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getWeekDates = () => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const getWorkoutsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return workouts.filter(w => w.scheduled_date === dateStr);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const getWorkoutTypeColor = (type) => {
    switch (type) {
      case 'strength':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cardio':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'hiit':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'mobility':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'recovery':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading workouts...</p>
        </div>
      </div>
    );
  }

  const weekDates = getWeekDates();

  return (
    <div className="space-y-6">
      {/* Header with View Toggle */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Workout Calendar</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('week')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors touch-manipulation ${
              viewMode === 'week'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            type="button"
            aria-label="Switch to week view"
            aria-pressed={viewMode === 'week'}
          >
            Week
          </button>
          <button
            onClick={() => setViewMode('day')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors touch-manipulation ${
              viewMode === 'day'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            type="button"
            aria-label="Switch to day view"
            aria-pressed={viewMode === 'day'}
          >
            Day
          </button>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between bg-white rounded-xl shadow-lg p-4">
        <button
          onClick={() => navigateWeek(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
          aria-label="Previous week"
          type="button"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <p className="text-sm text-gray-600">
            {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} -{' '}
            {weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </p>
        </div>
        <button
          onClick={() => navigateWeek(1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
          aria-label="Next week"
          type="button"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
        <button
          onClick={() => setCurrentDate(new Date())}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors touch-manipulation"
          type="button"
          aria-label="Go to today"
        >
          Today
        </button>
      </div>

      {/* Calendar View */}
      {viewMode === 'week' ? (
        <div className="grid grid-cols-7 gap-2">
          {weekDates.map((date, index) => {
            const dayWorkouts = getWorkoutsForDate(date);
            const isToday = date.toDateString() === new Date().toDateString();
            const isPast = date < new Date() && !isToday;

            return (
              <div
                key={index}
                className={`bg-white rounded-lg shadow p-3 min-h-[150px] ${
                  isToday ? 'ring-2 ring-primary-600' : ''
                } ${isPast ? 'opacity-60' : ''}`}
              >
                <div className="text-center mb-2">
                  <p className="text-xs text-gray-500 uppercase">
                    {date.toLocaleDateString('en-US', { weekday: 'short' })}
                  </p>
                  <p
                    className={`text-lg font-bold ${
                      isToday ? 'text-primary-600' : 'text-gray-900'
                    }`}
                  >
                    {date.getDate()}
                  </p>
                </div>
                <div className="space-y-1">
                  {dayWorkouts.length > 0 ? (
                    dayWorkouts.map((workout) => (
                      <button
                        key={workout.id}
                        className={`text-xs p-2 rounded border w-full text-left ${getWorkoutTypeColor(
                          workout.workout_type
                        )} cursor-pointer hover:opacity-80 transition-opacity touch-manipulation`}
                        onClick={() => setSelectedDate(date)}
                        type="button"
                        aria-label={`View workout: ${workout.workout_name}`}
                      >
                        <div className="flex items-center gap-1 mb-1">
                          <Dumbbell className="w-3 h-3" />
                          <span className="font-medium truncate">{workout.workout_name}</span>
                        </div>
                        {workout.duration_minutes && (
                          <div className="flex items-center gap-1 text-xs opacity-75">
                            <Clock className="w-3 h-3" />
                            <span>{workout.duration_minutes} min</span>
                          </div>
                        )}
                      </button>
                    ))
                  ) : workout.rest_day ? (
                    <div className="text-xs text-center text-gray-400 py-2">Rest Day</div>
                  ) : (
                    <div className="text-xs text-center text-gray-300 py-2">No workout</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            {selectedDate
              ? selectedDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })
              : currentDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}
          </h3>
          {getWorkoutsForDate(selectedDate || currentDate).length > 0 ? (
            <div className="space-y-4">
              {getWorkoutsForDate(selectedDate || currentDate).map((workout) => (
                <motion.div
                  key={workout.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-lg border ${getWorkoutTypeColor(workout.workout_type)}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">{workout.workout_name}</h4>
                      <p className="text-sm text-gray-600 capitalize">{workout.workout_type}</p>
                    </div>
                    {workout.duration_minutes && (
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{workout.duration_minutes} min</span>
                      </div>
                    )}
                  </div>
                  {workout.instructions && (
                    <p className="text-sm text-gray-700 mt-2">{workout.instructions}</p>
                  )}
                  {workout.exercise_order && workout.exercise_order.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-600 mb-1">
                        {workout.exercise_order.length} exercises
                      </p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No workouts scheduled for this day</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
